// import './AtomicComponents/translate/i18';
// import React, { Suspense } from 'react';
// import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// import Layout from './components/layout';
// import './App.css';
// import Loader from './components/common/Loader/index';
// import SignIn from './pages/authentication/SignIn';
// import routes from './routes';
// import NotFound from "./pages/NotFound/index";
// import { FavoritesProvider } from './AtomicComponents/FavoritesContext';
// import TopLoad from './AtomicComponents/TopLoad';

// function App() {
//   return (
//     <FavoritesProvider>
//       <Router>
//         <TopLoad />
//         <Suspense fallback={<Loader fullScreen={true} />}>
//           <Routes>
//             <Route path="/" element={<Layout />}>
//               <Route index element={<Navigate to="/home" replace />} />

//               <Route path="signin" element={<SignIn />} />

//               {routes.map(({ path, component: Component, name }) => (
//                 <Route
//                   key={name}
//                   path={path}
//                   element={<Component />}
//                 />
//               ))}
//             </Route>

//             <Route path="*" element={<NotFound />} />
//           </Routes>
//         </Suspense>
//       </Router>
//     </FavoritesProvider>
//   );
// }

// export default App;

import './AtomicComponents/translate/i18';
import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import Layout from './components/layout';
import './App.css';
import Loader from './components/common/Loader/index';
import SignIn from './pages/authentication/SignIn';
import routes from './routes';
import NotFound from "./pages/NotFound/index";
import { FavoritesProvider } from './AtomicComponents/FavoritesContext';
import TopLoad from './AtomicComponents/TopLoad';

/* =========== helpers =========== */
const isTokenValid = () => {
  const token = localStorage.getItem('Finder-token');
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return !exp || exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const getUserRole = () => (localStorage.getItem('userRole') || '').toLowerCase();

/* =========== guards (useNavigate) =========== */
function RequireAuthNav({ allowRoles }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // not logged in → go to signin
    if (!isTokenValid()) {
      navigate('/signin', {
        replace: true,
        state: { from: { pathname: location.pathname, search: location.search } },
      });
      return;
    }
    // role-gated?
    if (Array.isArray(allowRoles) && allowRoles.length) {
      const role = getUserRole();
      if (!allowRoles.map(r => r.toLowerCase()).includes(role)) {
        // user (or other role) trying to access admin/agent area → send to /home
        navigate('/home', { replace: true });
        return;
      }
    }
    setReady(true);
  }, [navigate, location, allowRoles]);

  if (!ready) return null; // avoid flash while deciding
  return <Outlet />;
}

// Prevent logged-in users from visiting /signin.
// Redirect by role: user -> /home, admin/agent -> /dashboard
function PublicOnlyRouteNav({ children }) {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isTokenValid()) {
      const role = getUserRole();
      const dest = (role === 'admin' || role === 'agent') ? '/dashboard' : '/home';
      navigate(dest, { replace: true });
      return;
    }
    setReady(true);
  }, [navigate]);

  if (!ready) return null;
  return children;
}

// Role-aware index landing for `/`
function RoleIndexRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const role = getUserRole();
    const dest = (role === 'admin' || role === 'agent') ? '/dashboard' : '/home';
    navigate(dest, { replace: true });
  }, [navigate]);

  return null;
}
/* =========================================== */

function App() {
  const publicRoutes = routes.filter(r => !r.protected && !r.roles);
  const userProtectedRoutes = routes.filter(r => r.protected && !r.roles);
  const adminAgentRoutes = routes.filter(r => Array.isArray(r.roles) && r.roles.some(x => ['admin','agent'].includes(x)));

  return (
    <FavoritesProvider>
      <Router>
        <TopLoad />
        <Suspense fallback={<Loader fullScreen={true} />}>
          <Routes>
            <Route path="/" element={<Layout />}>
              {/* role-aware landing */}
              <Route index element={<RoleIndexRedirect />} />

              {/* signin: keep logged-in folks out by role */}
              <Route
                path="signin"
                element={
                  <PublicOnlyRouteNav>
                    <SignIn />
                  </PublicOnlyRouteNav>
                }
              />

              {/* public routes */}
              {publicRoutes.map(({ path, component: Component, name }) => (
                <Route key={name} path={path} element={<Component />} />
              ))}

              {/* protected (any logged-in role) */}
              <Route element={<RequireAuthNav />}>
                {userProtectedRoutes.map(({ path, component: Component, name }) => (
                  <Route key={name} path={path} element={<Component />} />
                ))}
              </Route>

              {/* admin/agent protected */}
              <Route element={<RequireAuthNav allowRoles={['admin','agent']} />}>
                {adminAgentRoutes.map(({ path, component: Component, name }) => (
                  <Route key={name} path={path} element={<Component />} />
                ))}
              </Route>
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </FavoritesProvider>
  );
}

export default App;
